import type { MedplumClient } from "@medplum/core";
import type { Practitioner, QuestionnaireResponse, Reference } from "@medplum/fhirtypes";

import * as Sentry from "@sentry/react";

import type { CreateLaborEncounterAmendCompletionProvenanceResult } from "@/encounters/components/EncounterLabor/types/labor-amend-completion-provenance";
import type {
  LaborAmendFlowsheetPersistSignAndLockPort,
  LaborAmendSignAndLockResult,
} from "@/encounters/components/EncounterLabor/types/labor-amend-sign-and-lock";
import {
  hasEncounterAmendmentFormChanges,
  shouldSkipEncounterAmendmentSave,
} from "@/encounters/components/EncounterLabor/utils/labor-amend-encounter-save-verification";
import { isLaborAmendPostEffectsLostError } from "@/encounters/components/EncounterLabor/utils/labor-amend-errors";
import { writeLaborAmendEncounterSavedToSession } from "@/encounters/components/EncounterLabor/utils/labor-amend-session-state";
import {
  LABOR_AMEND_COMPLETION_PROVENANCE_CREATE_FAILED_MESSAGE,
  LaborAmendCompletionProvenanceFailureReason,
} from "@/encounters/constants/labor-amend-completion-provenance-failure-reason";
import { createLaborEncounterAmendCompletionProvenance } from "@/encounters/utils/create-labor-encounter-amend-completion-provenance";
import { reportLaborEncounterAmendProvenanceFailure } from "@/encounters/utils/report-labor-encounter-amend-provenance-failure";
import { EncounterAmendmentFormType } from "@/encounters/zod-schemas/encounter-answers";

export interface ExecuteLaborAmendSignAndLockParameters {
  medplum: MedplumClient;
  clientId: string;
  encounterId: string;
  questionnaireResponse: QuestionnaireResponse | undefined;
  formValues: EncounterAmendmentFormType;
  inMemorySavedEncounterId: string | undefined;
  practitionerReference: Reference<Practitioner> | undefined;
  saveEncounterAmendment: (values: EncounterAmendmentFormType) => Promise<QuestionnaireResponse>;
  flowsheetPersist: LaborAmendFlowsheetPersistSignAndLockPort;
}

/**
 * Saves the encounter amendment (when needed), stamps parent version, drains the flowsheet queue,
 * and runs post-amendment effects. Does not navigate or show toasts.
 */
export async function executeLaborAmendSignAndLock({
  medplum,
  clientId,
  encounterId,
  questionnaireResponse,
  formValues,
  inMemorySavedEncounterId,
  practitionerReference,
  saveEncounterAmendment,
  flowsheetPersist,
}: ExecuteLaborAmendSignAndLockParameters): Promise<LaborAmendSignAndLockResult> {
  if (!questionnaireResponse) {
    return {
      ok: false,
      reason: "encounter_save_failed",
      errorMessage: "Encounter questionnaire data is not loaded.",
    };
  }

  const skipEncounterSave =
    shouldSkipEncounterAmendmentSave({
      encounterId,
      questionnaireResponse,
      inMemorySavedEncounterId,
    }) && !hasEncounterAmendmentFormChanges(formValues, questionnaireResponse);

  let savedQuestionnaireResponse = questionnaireResponse;

  if (!skipEncounterSave) {
    try {
      savedQuestionnaireResponse = await saveEncounterAmendment(formValues);
      writeLaborAmendEncounterSavedToSession(encounterId);
    } catch (error) {
      return {
        ok: false,
        reason: "encounter_save_failed",
        errorMessage: error instanceof Error ? error.message : String(error),
      };
    }
  }

  const parentVersionId = savedQuestionnaireResponse.meta?.versionId;
  if (parentVersionId) {
    flowsheetPersist.setEncounterQuestionnaireParentVersionId(parentVersionId);
  }

  let flushResult;
  try {
    flushResult = await flowsheetPersist.drainFlowsheetPersistQueue();
  } catch (error) {
    return {
      ok: false,
      reason: "flowsheet_flush_failed",
      errorMessage: error instanceof Error ? error.message : String(error),
      savedQuestionnaireResponse,
    };
  }

  if (!flushResult.ok) {
    return {
      ok: false,
      reason: "flowsheet_flush_failed",
      errorMessage: flushResult.error.message,
      savedQuestionnaireResponse,
    };
  }

  try {
    await flowsheetPersist.runPostAmendmentEffects();
  } catch (error) {
    if (isLaborAmendPostEffectsLostError(error)) {
      return {
        ok: false,
        reason: "post_effects_lost",
        savedQuestionnaireResponse,
      };
    }

    return {
      ok: false,
      reason: "post_effects_failed",
      errorMessage: error instanceof Error ? error.message : String(error),
      savedQuestionnaireResponse,
    };
  }

  flowsheetPersist.finalizePostAmendmentEffects();

  const amendProvenanceSnapshot = flowsheetPersist.peekAmendProvenanceSnapshot();

  let provenanceResult: CreateLaborEncounterAmendCompletionProvenanceResult;
  try {
    provenanceResult = await createLaborEncounterAmendCompletionProvenance({
      medplum,
      clientId,
      encounterId,
      encounterQuestionnaireResponse: savedQuestionnaireResponse,
      practitionerReference,
      formValues,
      amendProvenanceSnapshot,
    });
  } catch (error) {
    Sentry.captureException(error, {
      level: "warning",
      extra: {
        encounterId,
        encounterQuestionnaireResponseId: savedQuestionnaireResponse.id,
        encounterQuestionnaireResponseVersionId: savedQuestionnaireResponse.meta?.versionId,
      },
    });
    provenanceResult = {
      ok: false,
      reason: LaborAmendCompletionProvenanceFailureReason.CreateFailed,
      details: LABOR_AMEND_COMPLETION_PROVENANCE_CREATE_FAILED_MESSAGE,
    };
  }

  if (!provenanceResult.ok) {
    reportLaborEncounterAmendProvenanceFailure({
      reason: provenanceResult.reason,
      details: provenanceResult.details,
      encounterId,
      encounterQuestionnaireResponseId: savedQuestionnaireResponse.id,
      encounterQuestionnaireResponseVersionId: savedQuestionnaireResponse.meta?.versionId,
    });
  } else {
    flowsheetPersist.finalizeAmendProvenanceSnapshot();
  }

  return {
    ok: true,
    savedQuestionnaireResponse,
    encounterSaveSkipped: skipEncounterSave,
    provenanceResult,
  };
}
